import { Button, Css, ModalBody, ModalFooter, ModalHeader, SelectCardGroup, useModal } from "@homebound/beam";
import { useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { Order } from "src/generated/graphqlTypes";
import { createMaterialDetailsUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { openNewTab } from "src/utils/window";
import {
  ChooseReplacementPossibleMaterialsQuery,
  useChooseReplacementPossibleMaterialsQuery,
} from "./ChooseReplacementModal.generated";

type ChooseReplacementModalProps = {
  /** The original PI being replaced; its placeholder requirements drive the search. */
  projectItemId: string;
  onSelect: (materialVariantId: string) => void;
  initialSelectedMaterialVariantId?: string;
};

export function ChooseReplacementModal(props: ChooseReplacementModalProps) {
  const { projectItemId, onSelect, initialSelectedMaterialVariantId = null } = props;
  const { closeModal } = useModal();
  const [search, setSearch] = useState("");
  const [selectedMaterialVariantId, setSelectedMaterialVariantId] = useState<string | null>(
    initialSelectedMaterialVariantId,
  );

  const query = useChooseReplacementPossibleMaterialsQuery({
    variables: { filter: { projectItemId, search, excludeCurrent: true }, orderBy: { lastUsedAt: Order.Desc } },
  });
  return (
    <>
      <ModalHeader>Choose the Replacement Product</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.df.jcfe.$}>
            <SearchBox onSearch={setSearch} updateQueryString={false} />
          </div>
          {queryResult(query, ({ materialVariantsForPlaceholder }) => (
            <ReplacementList
              candidates={materialVariantsForPlaceholder}
              selectedMaterialVariantId={selectedMaterialVariantId}
              onChange={setSelectedMaterialVariantId}
            />
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
        <Button
          label="Select"
          disabled={!selectedMaterialVariantId}
          onClick={() => {
            onSelect(selectedMaterialVariantId!);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type ReplacementListProps = {
  candidates: ChooseReplacementPossibleMaterialsQuery["materialVariantsForPlaceholder"];
  selectedMaterialVariantId: string | null;
  onChange: (materialVariantId: string | null) => void;
};

function ReplacementList(props: ReplacementListProps) {
  const { candidates, selectedMaterialVariantId, onChange } = props;
  const options = useMemo(
    () =>
      candidates.map((mv) => ({
        value: mv.id,
        label: mv.listing.brand?.name ?? mv.listing.name ?? "",
        description: mv.displayName,
        image: mv.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL,
        link: { label: "More Info", onClick: () => openNewTab(createMaterialDetailsUrl(mv.id)) },
      })),
    [candidates],
  );

  if (candidates.isEmpty) {
    return (
      <div css={Css.sm.gray700.$} data-testid="noResults">
        No matching products found.
      </div>
    );
  }

  return (
    <SelectCardGroup
      label="Replacement Products"
      options={options}
      value={selectedMaterialVariantId}
      onChange={onChange}
      layout="horizontal"
      view="grid"
    />
  );
}
